/**
 * 日付の入力(2026-09-10 指示: カレンダーの 土曜は青・日曜と祝日は赤)。
 * ブラウザ標準の日付入力ではカレンダーの色を変えられないため、同じ見た目の自前カレンダーを出す。
 * キーボードでも入れられるよう、テキスト(YYYY/MM/DD)の直接入力も受け付ける。
 */
import { useEffect, useRef, useState } from 'react';
import { IconCalendar, IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { addDays, daysInMonth } from '@core/dashboard/calc';
import { weekdayLabel } from '@core/lib/date';
import { dayColorOf, holidayName } from '@core/lib/holidays';
import type { DateStr } from '@core/lib/types';

const pad = (n: number) => String(n).padStart(2, '0');
const DOW = ['月', '火', '水', '木', '金', '土', '日'];

export default function DateField({ value, onChange, style }: { value: DateStr; onChange: (d: DateStr) => void; style?: React.CSSProperties }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(value.slice(0, 7));
  const [text, setText] = useState(value.replaceAll('-', '/'));
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setText(value.replaceAll('-', '/'));
    setMonth(value.slice(0, 7));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const commitText = (s: string) => {
    const m = s.trim().replaceAll('/', '-').match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!m) {
      setText(value.replaceAll('-', '/'));
      return;
    }
    const d = `${m[1]}-${pad(Number(m[2]))}-${pad(Number(m[3]))}` as DateStr;
    onChange(d);
  };

  const shiftMonth = (delta: number) => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(`${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}`);
  };

  /** 月曜始まりのマス目(前後の月は空欄) */
  const cells: (DateStr | null)[] = [];
  const first = `${month}-01` as DateStr;
  const lead = (new Date(`${first}T00:00:00Z`).getUTCDay() + 6) % 7;
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth(month); d++) cells.push(`${month}-${pad(d)}` as DateStr);

  return (
    <div ref={boxRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitText((e.target as HTMLInputElement).value);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        aria-label="日付"
        style={{ height: 30, width: 116, padding: '0 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 12.5, ...style }}
      />
      <button type="button" className="chip" style={{ marginLeft: 2, padding: '0 8px' }} aria-label="カレンダーを開く" onClick={() => setOpen((v) => !v)}>
        <IconCalendar size={14} />
      </button>
      {open && (
        <div className="cal-pop">
          <div className="cal-head">
            <button type="button" className="chip" onClick={() => shiftMonth(-1)} aria-label="前の月">
              <IconChevronLeft size={13} />
            </button>
            <b>
              {month.slice(0, 4)}年{Number(month.slice(5))}月
            </b>
            <button type="button" className="chip" onClick={() => shiftMonth(1)} aria-label="次の月">
              <IconChevronRight size={13} />
            </button>
          </div>
          <div className="cal-grid">
            {DOW.map((d) => (
              <div key={d} className={`cal-dow${d === '土' ? ' sat' : d === '日' ? ' sun' : ''}`}>
                {d}
              </div>
            ))}
            {cells.map((d, i) =>
              d === null ? (
                <div key={`e${i}`} />
              ) : (
                <button
                  key={d}
                  type="button"
                  className={`cal-day${d === value ? ' on' : ''}${dayColorOf(d) === 'red' ? ' sun' : dayColorOf(d) === 'blue' ? ' sat' : ''}`}
                  title={holidayName(d) ?? undefined}
                  onClick={() => {
                    onChange(d);
                    setOpen(false);
                  }}
                >
                  {Number(d.slice(8))}
                </button>
              ),
            )}
          </div>
          <div className="cal-foot">
            <button type="button" className="txn-link" onClick={() => { onChange(addDays(value, -1)); }}>
              前日
            </button>
            <span className="hint">
              {value.replaceAll('-', '/')}
              {weekdayLabel(value)}
              {holidayName(value) ? ` ${holidayName(value)}` : ''}
            </span>
            <button type="button" className="txn-link" onClick={() => { onChange(addDays(value, 1)); }}>
              翌日
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
